import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../../lib/api-client.js';
import type { FinancePeriod } from './use-finance-summary.js';

export interface TierDistribution {
  tier: string;
  count: number;
  pct: number;
}

export interface DriverEconByTerminal {
  isp: string;
  drivers: DriverEconDriver[];
  total_stops: number;
  total_days: number;
  total_pay: number;
  pay_count: number;
  avg_stops: number;
  avg_pay: number;
}

export interface DriverEconDriver {
  driver_id?: string;
  name: string;
  facility?: string;
  isp?: string | null;
  total_stops: number;
  days_worked: number;
  avg_stops_day: number;
  total_pay: number | null;
  tier: string;
}

export interface DriverEconScatter {
  driver_id?: string;
  name: string;
  avg_stops: number;
  total_pay: number;
  isp?: string | null;
  tier: string;
}

export interface DriverEconomicsData {
  period: FinancePeriod;
  tier_distribution: TierDistribution[];
  by_terminal: DriverEconByTerminal[];
  scatter: DriverEconScatter[];
  top_performers: DriverEconDriver[];
  bottom_performers: DriverEconDriver[];
  company_avg_stops: number;
  company_avg_pay_per_stop: number;
  total_drivers: number;
}

interface Response extends DriverEconomicsData { error?: string; }

export function useFinanceDriverEconomics(periodType: string) {
  const [data, setData] = useState<DriverEconomicsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams();
      if (periodType) params.set('period', periodType);
      const resp = await apiFetch<Response>(`/api/finance/driver-economics?${params.toString()}`);
      if (resp?.error) throw new Error(resp.error);
      setData(resp);
    } catch (err) { setData(null); setError(err instanceof Error ? err.message : 'Driver economics load failed'); }
    finally { setLoading(false); }
  }, [periodType]);

  useEffect(() => { load(); }, [load]);
  return { data, loading, error, refresh: load };
}
